/**
 * 系统配置 — GET /api/admin/config，PUT /api/admin/config/:key
 */
import { useState } from 'react';
import { Button, Form, Input, Modal, Table, message } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface ConfigRow {
  k: string;
  v: string;
  note: string | null;
  updated_by: number | null;
  update_time: string;
}

export function SystemConfigPage() {
  const [editRow, setEditRow] = useState<ConfigRow | null>(null);
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canEdit = hasNode('system.config.edit');

  const { data = [], isFetching } = useQuery({
    queryKey: ['admin', 'config'],
    queryFn: () => api.get<ConfigRow[]>('/api/admin/config'),
  });

  async function save(values: { v: string; note?: string }) {
    if (!editRow) return;
    try {
      await api.put(`/api/admin/config/${encodeURIComponent(editRow.k)}`, {
        v: values.v,
        note: values.note,
      });
      message.success('已保存');
      setEditRow(null);
      qc.invalidateQueries({ queryKey: ['admin', 'config'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '保存失败');
    }
  }

  const columns = [
    { title: '键', dataIndex: 'k', width: 220 },
    { title: '值', dataIndex: 'v', ellipsis: true },
    { title: '备注', dataIndex: 'note', ellipsis: true, render: (v: string | null) => v ?? '—' },
    {
      title: '操作',
      key: 'op',
      width: 100,
      render: (_: unknown, row: ConfigRow) =>
        canEdit ? (
          <Button
            type="link"
            size="small"
            onClick={() => {
              setEditRow(row);
              form.setFieldsValue({ v: row.v, note: row.note ?? '' });
            }}
          >
            编辑
          </Button>
        ) : null,
    },
  ];

  return (
    <div>
      <Table<ConfigRow>
        size="small"
        bordered
        rowKey="k"
        loading={isFetching}
        dataSource={data}
        columns={columns}
        pagination={false}
      />

      <Modal
        open={editRow !== null}
        title={`编辑配置 — ${editRow?.k ?? ''}`}
        onCancel={() => setEditRow(null)}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save}>
          <Form.Item name="v" label="值" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
